import { Button, Card, Flex, Heading, Text } from '@radix-ui/themes'
import { PlusIcon } from '@radix-ui/react-icons'
import { useQuery } from '@tanstack/react-query'
import { useTRPC } from '~/utils/trpc'
import { TicketRow, daysAgoLabel } from './TicketRow'
import { NewTicketDialog } from './NewTicketDialog'

interface InstallationTicketsProps {
  installationId: number
  siteContactEmail?: string | null
  siteContactName?: string | null
}

export const InstallationTickets = ({
  installationId,
  siteContactEmail,
  siteContactName
}: InstallationTicketsProps) => {
  const trpc = useTRPC()
  const ticketsQuery = useQuery(trpc.ops.freshdesk.listForInstallation.queryOptions({ installationId }))

  // Hide the section entirely when Freshdesk isn't configured for this environment.
  if (ticketsQuery.data && !ticketsQuery.data.configured) return null

  const tickets = ticketsQuery.data?.tickets ?? []

  return (
    <Card className="h-full">
      <Flex direction="column" gap="3">
        <Flex align="center" justify="between" gap="3">
          <Heading size="3">Support tickets</Heading>
          <NewTicketDialog
            installationId={installationId}
            defaultRequesterEmail={siteContactEmail}
            defaultRequesterName={siteContactName}
            trigger={
              <Button size="1" variant="soft">
                <PlusIcon />
                New ticket
              </Button>
            }
          />
        </Flex>
        {ticketsQuery.isLoading ? (
          <Text size="2" color="gray">
            Loading…
          </Text>
        ) : ticketsQuery.error ? (
          <Text size="2" color="red">
            {ticketsQuery.error.message}
          </Text>
        ) : tickets.length === 0 ? (
          <Text size="2" color="gray">
            No support tickets for this installation.
          </Text>
        ) : (
          <Flex direction="column" gap="3">
            {tickets.map((ticket) => (
              <TicketRow
                key={ticket.id}
                ticket={ticket}
                footer={
                  <Text size="1" color="gray">
                    Opened {daysAgoLabel(ticket.createdAt)} · Last updated {daysAgoLabel(ticket.updatedAt)}
                  </Text>
                }
              />
            ))}
          </Flex>
        )}
      </Flex>
    </Card>
  )
}
